import sum from 'lodash/sum';
import type { BufferingMode } from '../../enums';
import type { DestroyOptions, LuminaryObject } from '../../types';
import calculateReadingDurations from '../../libs/calculateReadingDurations';
import { assertHasOwnProperties, assertObject } from '../../validators';
import { Config } from '../../video-data/config';
import { BaseAsset } from '../asset/BaseAsset';
import type { BaseAssetSettings } from '../asset/BaseAsset';
import { comparer, observable, reaction, runInAction, untracked } from '../reactivity';
import { ConcurrentTimeline } from '../../libs/ConcurrentTimeline';
import { Subscene } from './Subscene';
import type { RemoveAnchoredPoints } from '../../libs/Curve';

export class SubsceneManager extends BaseAsset implements LuminaryObject {
    override _classType = 'SubsceneManager';

    _timeline = new ConcurrentTimeline({
        name: 'subscenes_timeline',
        duration: Config.DEFAULT_SCENE_DURATION,
    });

    /**
     * Whether video data is being loaded. The duration derivation yields nothing while it is set, so a load
     * assigns durations itself rather than having them recalculated from half-loaded subscenes.
     */
    @observable accessor #isVideoDataLoading = false;
    /**
     * The duration set at scene level, pending distribution across the subscenes. The derivation prefers it
     * over the manager's own duration, which is derived from the subscenes it is about to write.
     */
    @observable accessor _durationToApply: number | null = null;

    // TODO(strict-ts): container type should match BaseAsset's once that constructor is typed
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: BaseAssetSettings) {
        super(container, settings);

        this.addReaction(
            reaction(
                () => this.#endAnimationAnchors(),
                (anchors) =>
                    this._batchRedraw(() => {
                        for (let i = 0; i < anchors.length; i += 3) {
                            const asset = anchors[i] as BaseAsset;
                            asset._animationAnchorsToRemove = anchors[
                                i + 2
                            ] as RemoveAnchoredPoints;
                        }
                    }),
                { equals: comparer.shallow, name: 'SubsceneManager.endAnimationAnchors' },
            ),
        );

        this.addReaction(
            reaction(
                () => ({
                    inputs: this.#durationInputs(),
                    subscenes: untracked(() => this._subscenes),
                }),
                ({ inputs, subscenes }) => {
                    if (inputs.length === 0) {
                        return;
                    }
                    const [duration, ...textLengths] = inputs;
                    runInAction(() => {
                        this._durationToApply = null;
                        this._batchRedraw(() =>
                            calculateReadingDurations(textLengths, duration).forEach(
                                (subsceneDuration, index) => {
                                    subscenes[index].duration = subsceneDuration;
                                },
                            ),
                        );
                    });
                },
                {
                    equals: (a, b) => comparer.shallow(a.inputs, b.inputs),
                    name: 'SubsceneManager.subsceneDurations',
                },
            ),
        );
    }

    /**
     * Called while seeking, forwarding a request to buffer any data (if need be) down the hierarchy.
     */
    async _propagateBuffering(bufferingMode: BufferingMode) {
        const activeSubscenes = this._requireSlide._getActiveSubscenesAt(
            this._requireSlide.currentTime,
        );
        await Promise.all(
            activeSubscenes.map(async (subscene) => {
                return subscene._propagateBuffering(bufferingMode);
            }),
        );
    }

    override get canPlay() {
        return this._children.every((asset) => asset.canPlay);
    }

    /**
     * This shouldn't be needed, but adding as the interface requires it. Ideally the
     * SubsceneManager isn't a "luminary object", but that requires a larger refactor.
     */
    _emitAssetsCanPlay() {
        console.warn(
            'SubsceneManager should not be forwarding canPlay events, the subscene' +
                ' should be sending these events to the slide. Forwarding for now anyway.',
        );
        if (this.slide) {
            this.slide.emit('assetsCanPlay');
        }
    }

    /**
     * Returns true if all assets (deeply) within this object have finished loading, or have
     * failed to load. If any assets are still loading or retrying a load this will be false.
     */
    get areAssetsDoneTryingToLoad(): boolean {
        return this._subscenes.every((subscene) => subscene.areAssetsDoneTryingToLoad);
    }

    /**
     * Returns true if all assets (deeply) within this object have successfully loaded.
     */
    override get isLoaded(): boolean {
        return this._children.every((asset) => asset.isLoaded);
    }

    override get _shouldLoad() {
        return true;
    }

    get duration(): number {
        return this._requireSlide.duration;
    }

    /**
     * @deprecated use {@link subscenes} instead
     * */
    get subslides(): readonly Subscene[] {
        return this.subscenes;
    }

    get subscenes(): readonly Subscene[] {
        return this.children as readonly Subscene[];
    }

    /**
     * The live subscenes array. Internal callers use this to avoid paying for the copy that
     * [subscenes]{@link SubsceneManager#subscenes} makes; `Scene` reads it on every deep `getAssets`, which the scene
     * reactions do on every membership change. It must never be handed outside luminary.
     *
     * @internal
     */
    get _subscenes(): readonly Subscene[] {
        return this._children as readonly Subscene[];
    }

    /**
     * Create and add a new subscene. By default, it will add the subscene at the end.
     *
     * One action for the whole insertion: the timeline, the copied content and the membership are three writes,
     * and a derivation waking between them sees a subscene that exists with no room in the scene to hold it.
     * @param duplicateContent - if content should be copied from the first subscene
     * @param index - if provided, the index where to insert the subscene
     * @param id - if provided, the id of the newly created subscene
     * @returns the newly created Subscene
     */
    addSubscene(duplicateContent = true, index?: number, id?: string, grow = false) {
        return runInAction(() => {
            const duration = this.duration;
            if (duration === 0) {
                throw Error('Invalid scene duration');
            }
            if (typeof index === 'undefined') {
                index = this._subscenes.length;
            }
            const sourceSubscene = this._subscenes[0];

            const subscene = new Subscene(this, {
                id,
                updateParentOnRedraw: true,
                fetchResources: this.fetchResources,
                timeline: this._globalTimeline,
                autoDraw: this.autoDraw,
                assetNetworkManager: this._assetNetworkManager,
                getAspectRatio: () => this._getAspectRatio(),
                reusableTextures: this._reusableTextures,
                getReferenceResolution: () => this.getReferenceResolution(),
                getOutputResolution: () => this.getOutputResolution(),
            });

            if (this.slide) {
                // Add the new subscene group's timeline to the scene's timeline without changing the scene's duration
                this._timeline.add(subscene._timeline, index, grow);
            }

            // Copied assets clamp their animations to the subscene they are built into, and a subscene joins the
            // timeline a millisecond long, which would flatten them. Split the scene across the subscenes it is
            // about to have, before any content arrives; the derivation settles on the same split afterwards.
            if (duplicateContent && sourceSubscene) {
                const seededDurations = calculateReadingDurations(
                    [...this.#getTextLengthArrayFromTextAssetChildren(), 0],
                    duration,
                );
                subscene.duration = seededDurations[seededDurations.length - 1];
                this._subscenes.forEach((existing, existingIndex) => {
                    existing.duration = seededDurations[existingIndex];
                });

                this.#copySubsceneContentFromSourceToTarget(sourceSubscene, subscene);
            }

            // addChild triggers a redraw so we have to do all of the wiring beforehand which is why this is run last.
            this.insertAssetAt(subscene, index);
            return subscene;
        });
    }

    /** @deprecated Use addSubscene instead */
    addSubslide(duplicateContent = true, index?: number, id?: string, grow = false) {
        return this.addSubscene(duplicateContent, index, id, grow);
    }

    /**
     * duplicates our scene's base subscene configuration onto the given subscene
     */
    #copySubsceneContentFromSourceToTarget(sourceSubscene: Subscene, targetSubscene: Subscene) {
        assertObject(sourceSubscene);
        assertObject(targetSubscene);

        // transfer over all child content
        const childrenConfig = sourceSubscene.saveChildren();
        targetSubscene.load(childrenConfig);

        targetSubscene.generateNewAssetIds();

        // update all text content to be blank (clear text)
        // we do this because when duplicating a subscene we don't want to copy over text
        targetSubscene.getAssetsByType(Config.TEXT_ASSET_TYPE).forEach((asset) => {
            asset.text = '';
        });
    }

    /**
     * One batch for the whole removal: detaching wakes the derivations that follow membership, and their
     * writes would otherwise announce separately from the removal itself.
     */
    removeSubscene(index: number) {
        this._batchRedraw(() =>
            runInAction(() => {
                if (!this.#detachSubscene(index)) {
                    return;
                }
                this.requiresRedraw = true;
            }),
        );
    }

    #detachSubscene(index: number): boolean {
        if (!this._subscenes.hasOwnProperty(index)) {
            return false;
        }
        this._timeline.remove(this._subscenes[index]._timeline);
        this._subscenes[index].destroy(true);
        return true;
    }

    /** @deprecated Use removeSubscene instead */
    removeSubslide(index: number) {
        this.removeSubscene(index);
    }

    generateNewAssetIds() {
        this._subscenes.forEach((subscene) => {
            subscene.generateNewAssetIds();
        });
    }

    /**
     * One action for the whole clear: `#detachSubscene` destroys a subscene, which is atomic on its own, but a
     * sequence of them is not — without this a derivation runs once per subscene against a partly emptied manager.
     */
    override clear() {
        runInAction(() => {
            const nsubscenes = this._subscenes.length;
            for (let i = 0; i < nsubscenes; i++) {
                this.#detachSubscene(0);
            }
            this._durationToApply = null;
            this.#isVideoDataLoading = false;
        });
    }

    override destroy(options?: DestroyOptions) {
        // The subscenes are this object's children, so `super.destroy` has already torn them down and emptied the
        // list. Anything iterating `subscenes` after this line runs against nothing.
        super.destroy(options);
    }

    override _build() {}

    get #hasAssetWithTextEditingEnabled() {
        return this._requireSlide
            .getAssetsByType(Config.TEXT_ASSET_TYPE)
            .some((textAsset) => textAsset.hasTextEditingEnabled);
    }

    _updateSubsceneDurationBasedOnTextLength(duration: number, batch = true) {
        const initAutoDraw = this.autoDraw;
        if (batch) {
            this.autoDraw = false;
        }

        const textLengths = this.#getTextLengthArrayFromTextAssetChildren();
        calculateReadingDurations(textLengths, duration).forEach((subsceneDuration, index) => {
            this._subscenes[index].duration = subsceneDuration;
        });

        if (batch) {
            this.autoDraw = initAutoDraw;
        }
    }

    #getTextLengthArrayFromTextAssetChildren() {
        return this._subscenes.map((subscene) => {
            return sum(
                subscene
                    .getAssetsByType(Config.TEXT_ASSET_TYPE)
                    .map((textAsset) => textAsset.text.length),
            );
        });
    }

    /**
     * The total to distribute followed by each subscene's text length, flat so a shallow comparer holds. Empty
     * while a load is in flight or a text asset is being edited, which are the two states the durations must
     * not be recalculated in.
     */
    #durationInputs(): number[] {
        if (
            this._subscenes.length === 0 ||
            this.#isVideoDataLoading ||
            this.#hasAssetWithTextEditingEnabled
        ) {
            return [];
        }
        return [
            this._durationToApply ?? this.duration,
            ...this.#getTextLengthArrayFromTextAssetChildren(),
        ];
    }

    /**
     * Each asset of each subscene followed by its animate-out behaviour and the anchor it should be left with:
     * an asset in the last subscene that animates out when preceding drops its end anchor, everything else
     * keeps both. The behaviour rides along because its setter writes an anchor of its own, which this has to
     * overwrite even when the anchor computed here has not moved. Flat so that a shallow comparer holds.
     */
    #endAnimationAnchors(): (BaseAsset | string | RemoveAnchoredPoints)[] {
        return this._subscenes.flatMap((subscene, index) => {
            const isLastSubscene = index === this._subscenes.length - 1;
            return [subscene, ...subscene.getAssets()].flatMap((asset: BaseAsset) => [
                asset,
                asset.animateOutBehaviour,
                isLastSubscene &&
                asset.animateOutBehaviour === Config.ANIMATE_OUT_BEHAVIOUR_WHEN_PRECEDING
                    ? Config.END_ANIMATION_CURVE_ANCHOR
                    : Config.NONE_ANIMATION_CURVE_ANCHOR,
            ]);
        });
    }

    override load(data: { subslides?: Record<string, unknown>[] } = {}) {
        assertObject(data);
        assertHasOwnProperties(data, ['subslides']);

        if (this._subscenes.length !== 0) {
            throw Error('Unable to load data, subscene length is not zero.');
        }

        const initAutoDraw = this.autoDraw;
        this.autoDraw = false;

        this.#isVideoDataLoading = true;

        data.subslides!.forEach((subsceneProperties, index) => {
            const subscene = this.addSubscene(
                false,
                index,
                subsceneProperties.id as string | undefined,
                true,
            );
            subscene.load(subsceneProperties);
        });

        this.#isVideoDataLoading = false;

        this.autoDraw = initAutoDraw;
        return this;
    }

    override save(allProperties = true, clone = false) {
        const data: { subslides: Record<string, unknown>[] } = { subslides: [] };
        this._subscenes.forEach((subscene) =>
            data.subslides.push(subscene.save(allProperties, clone)),
        );
        return data;
    }
}
